import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, IconButton, Typography, darken, useMediaQuery, useTheme } from '@mui/material';
import { format } from 'date-fns';
import { ru, uk } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchActiveQuiz } from '../../actions/quiz';
import QuizTypes from '../../constants/quiz';
import usePhoneCheck from '../../hooks/usePhoneCheck';

export const ShowQuizResult = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const theme = useTheme();

    const { result, selectedQuizId } = useSelector((state: any) => state.quiz);

    const isMedia600 = useMediaQuery(theme.breakpoints.down('sm'));
    const isMobile = usePhoneCheck();

    const lang = localStorage.getItem('lang');
    const mode = theme.palette.mode;

    const locale = lang === 'ru' ? ru : uk;

    const handleQuizRetry = () => {
        dispatch(fetchActiveQuiz(selectedQuizId));
        dispatch({ type: QuizTypes.SET_RESET_RESULT, payload: {} });
        dispatch({ type: QuizTypes.SET_SHOW_RESULT, payload: false });
    };

    const success = result?.score >= 80;

    return (
        <Box
            sx={{
                width: isMobile ? '98%' : 900,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                bgcolor: mode === 'dark' ? 'grey.800' : 'background.paper',
                p: 3,
                py: 5,
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMedia600 ? 'column' : 'row',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    width: '100%',
                    gap: 3,
                }}
            >
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <CircularProgress variant="determinate" value={100} size={120} sx={{ color: 'transparent' }} />
                    <CircularProgress
                        variant="determinate"
                        value={result?.score ?? 0}
                        size={120}
                        sx={{
                            position: 'absolute',
                            left: 0,
                            color: success ? 'success.main' : 'warning.main',
                            transform: 'rotate(126deg)',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {success ? (
                            <CheckCircleOutlineIcon color="success" fontSize="large" />
                        ) : (
                            <CancelIcon color="warning" fontSize="large" />
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isMedia600 ? 'center' : 'flex-start',
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography>{t('quiz.score')}</Typography>
                        <Typography fontWeight="bold" align={isMedia600 ? 'center' : 'left'}>
                            {result?.score} %
                        </Typography>
                    </Box>

                    <Box>
                        <Typography>{t('quiz.attempts_used')}</Typography>
                        <Typography fontWeight="bold" align={isMedia600 ? 'center' : 'left'}>
                            {result?.attempts}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isMedia600 ? 'center' : 'flex-start',
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography>{t('quiz.min_score')}</Typography>
                        <Typography fontWeight="bold" align={isMedia600 ? 'center' : 'left'}>
                            {result?.min_score} %
                        </Typography>
                    </Box>

                    <Box>
                        <Typography>{t('quiz.completion_date')}</Typography>
                        <Typography fontWeight="bold" align={isMedia600 ? 'center' : 'left'}>
                            {result?.updated_at &&
                                format(new Date(result.updated_at), "d MMM yyyy',' HH:mm", { locale })}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Button
                onClick={handleQuizRetry}
                sx={{
                    mt: 5,
                    bgcolor: '#58a315',
                    color: '#fff',
                    '&:hover': {
                        bgcolor: darken('#58a315', 0.2),
                    },
                }}
            >
                {t('quiz.quiz_retry')}
            </Button>

            <IconButton
                aria-label="close"
                onClick={() =>
                    dispatch({
                        type: QuizTypes.SET_QUIZ_OPEN,
                        payload: false,
                    })
                }
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: 'text.primary',
                }}
            >
                <CloseIcon />
            </IconButton>
        </Box>
    );
};
